package top.wcpe.allincore.ui.runtime.theme

import top.wcpe.allincore.ui.runtime.internal.concurrent.ConcurrentMutableMap
import kotlin.jvm.JvmField

/**
 * 主题预设管理器：持有 IR 中编译期内嵌的所有 scheme 的 token 表，在 `local.setTheme(name)` 触发时
 * 懒构造 [ControlTheme] 并缓存。
 *
 * 每个 IrSession 拥有一份独立实例，session 关闭时随 EvalContext 一并 GC。
 *
 * 缓存策略：使用 [ConcurrentMutableMap.computeIfAbsent]，首次切换某 scheme 时触发
 * [ControlTheme.fromLookup]，后续 O(1) 命中。当前主题数量受控（~10），无需软引用或 LRU；
 * 若未来支持用户自定义动态主题导致数量不可控，需评估缓存膨胀。
 *
 * 线程安全：构造期一次性建好 available 不可变 map；运行时仅读 available + 写 cache
 * （[ConcurrentMutableMap]，JVM actual = ConcurrentHashMap），可在多线程间共享。
 */
class ThemeManager(availableThemes: Map<String, Map<String, Any?>>?) {

    private val available: Map<String, ThemeLookup> =
        if (availableThemes == null || availableThemes.isEmpty()) {
            emptyMap()
        } else {
            // 保序 + 只读：按编译期插入顺序构造，不可变对外
            availableThemes.mapValuesTo(LinkedHashMap(availableThemes.size)) { (_, tokens) ->
                ThemeLookup.of(tokens)
            }
        }

    private val cache = ConcurrentMutableMap<String, ControlTheme>()

    /** 是否包含指定 scheme。 */
    fun contains(scheme: String?): Boolean = scheme != null && available.containsKey(scheme)

    /** 是否为空（IR 未声明任何 scheme 时返回 true）。 */
    fun isEmpty(): Boolean = available.isEmpty()

    /** 已注册的 scheme 名集合，按编译期插入顺序保序。 */
    fun availableNames(): Set<String> = available.keys

    /** 按 token 值匹配当前 scheme；多个 scheme 内容相同时返回首个编译期顺序命中项。 */
    internal fun schemeOf(lookup: ThemeLookup): String? =
        available.entries.firstOrNull { it.value == lookup }?.key

    /**
     * 取指定 scheme 的原始 token [ThemeLookup]，供 `local.setTheme` 把组件 props 的 `theme.X`
     * 切换到目标主题的 token 值。scheme 未注册时返回 [ThemeLookup.empty]。
     */
    fun lookupOf(scheme: String?): ThemeLookup = available[scheme] ?: ThemeLookup.empty()

    /**
     * 解析 scheme 为 [ControlTheme]：首次调用触发 [ControlTheme.fromLookup]，后续命中缓存。
     *
     * @throws IllegalArgumentException 若 scheme 未注册
     */
    fun resolve(scheme: String): ControlTheme {
        require(available.containsKey(scheme)) { "未知 theme: $scheme" }
        return cache.computeIfAbsent(scheme) { s ->
            ControlTheme.fromLookup(available.getValue(s), ControlTheme.defaultFallback())
        }
    }

    companion object {
        /** 共享空实例：旧 IR（v12 及以下）解码出空 availableThemes 时使用。 */
        @JvmField
        val EMPTY = ThemeManager(emptyMap())
    }
}
